/**
 * Content Compliance API service
 *
 * Wraps /api/compliance/* endpoints (regulated entity flag, settings,
 * expiring artifacts, audit dashboard) and surfaces friendly TypeScript types.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
const API_VERSION = '1.0.0';

export type ComplianceMode = 'standard' | 'regulated';

export interface ContentComplianceSettings {
  cbo_id?: number;
  mode?: ComplianceMode;
  default_expiration_required: boolean;
  default_expiration_days: number | null;
  allow_expired_in_communications: boolean;
  notify_days_before: number;
  default_template_approval_required: boolean;
  default_content_approval_required: boolean;
  default_workflow_approval_required: boolean;
  approval_levels: number;
  enforce_consent_on_all_paths: boolean;
  block_expired_in_communications: boolean;
  require_approved_template_for_send: boolean;
  allow_compliance_override: boolean;
  override_requires_reason: boolean;
}

export interface ExpiringArtifact {
  id: number;
  name?: string;
  asset_type?: string;
  regulated_category?: string | null;
  expiration_date: string;
  compliance_status?: string;
  allow_use_after_expiry?: boolean;
}

export interface ComplianceDashboard {
  pending_approvals: number;
  expired_assets: number;
  expiring_soon: number;
  overrides_30d: number;
  mode: ComplianceMode;
}

class ContentComplianceAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  async getSettings(cboId: number): Promise<ContentComplianceSettings> {
    const r = await this.client.get('/api/compliance/settings', { params: { cbo_id: cboId } });
    return r.data;
  }

  async updateSettings(cboId: number, payload: Partial<ContentComplianceSettings>) {
    const r = await this.client.patch('/api/compliance/settings', payload, {
      params: { cbo_id: cboId },
    });
    return r.data as ContentComplianceSettings;
  }

  async setMode(cboId: number, mode: ComplianceMode) {
    const r = await this.client.patch(
      '/api/compliance/mode',
      { mode },
      { params: { cbo_id: cboId } },
    );
    return r.data as { cbo_id: number; mode: ComplianceMode; settings: ContentComplianceSettings };
  }

  async listPermissions() {
    const r = await this.client.get('/api/compliance/permissions');
    return r.data as Array<{ permission_key: string; description: string; default_roles: string[] }>;
  }

  async listExpiring(cboId: number, withinDays = 30) {
    const r = await this.client.get('/api/compliance/expiring', {
      params: { cbo_id: cboId, within_days: withinDays },
    });
    return r.data as { media_assets: ExpiringArtifact[]; documents: ExpiringArtifact[]; within_days: number };
  }

  async listEnforcementEvents(cboId: number, limit = 100) {
    const r = await this.client.get('/api/compliance/enforcement-events', {
      params: { cbo_id: cboId, limit },
    });
    return r.data as Array<Record<string, any>>;
  }

  async getDashboard(cboId: number) {
    const r = await this.client.get('/api/compliance/dashboard', { params: { cbo_id: cboId } });
    return r.data as ComplianceDashboard;
  }

  // --- Media asset-type policies ------------------------------------------

  async listAssetTypePolicies() {
    const r = await this.client.get('/api/media/policies');
    return r.data as AssetTypePolicy[];
  }

  async upsertAssetTypePolicy(policy: AssetTypePolicyUpsert) {
    return (await this.client.post('/api/media/policies', policy)).data;
  }

  async deleteAssetTypePolicy(policyId: number) {
    return (await this.client.delete(`/api/media/policies/${policyId}`)).data;
  }
}

export interface AssetTypePolicy {
  id: number;
  asset_type: string;
  regulated_category: string | null;
  expiration_policy: 'never' | 'always' | 'sometimes';
  default_expiration_days: number | null;
  allow_use_after_expiry: boolean;
  notify_days_before: number;
  requires_approval: boolean;
  is_active: boolean;
}

export interface AssetTypePolicyUpsert {
  asset_type: string;
  regulated_category?: string | null;
  expiration_policy: 'never' | 'always' | 'sometimes';
  default_expiration_days?: number | null;
  allow_use_after_expiry?: boolean;
  notify_days_before?: number;
  requires_approval?: boolean;
  is_active?: boolean;
}

export const contentComplianceAPI = new ContentComplianceAPI();
